// Constantes para el módulo de Transportistas
export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============= HELPERS PARA VENCIMIENTOS =============
/**
 * Verifica el estado de vencimiento de una fecha
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @returns {object} { status: 'expired'|'warning'|'ok', daysUntil: number, label: string }
 */
export const getExpiryStatus = (dateStr) => {
  if (!dateStr) return { status: 'unknown', daysUntil: null, label: 'Sin fecha' };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = new Date(dateStr);
  expiryDate.setHours(0, 0, 0, 0);
  
  const diffTime = expiryDate - today;
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (daysUntil < 0) {
    return { status: 'expired', daysUntil, label: `Vencido hace ${Math.abs(daysUntil)} días` };
  } else if (daysUntil <= 30) {
    return { status: 'warning', daysUntil, label: `Vence en ${daysUntil} días` };
  }
  return { status: 'ok', daysUntil, label: 'Vigente' };
};

/**
 * Formatea una fecha a DD/MM/YYYY
 */
export const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '-';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

// Estructura de campos para carga masiva
export const COMPANY_FIELDS = [
  // Datos de facturación
  { key: 'name', label: 'Razón Social (Facturador)', required: true },
  { key: 'alias', label: 'Alias', required: false },
  { key: 'cuit_rut', label: 'CUIT/RUT', required: true, unique: true },
  { key: 'address', label: 'Dirección', required: true },
  { key: 'city', label: 'Ciudad', required: true },
  { key: 'province', label: 'Provincia', required: true },
  // Datos de permisos
  { key: 'permit_holder_name', label: 'Razón Social (Dueño Permisos)', required: false },
  { key: 'international_permit', label: 'Permiso Internacional', required: true },
  { key: 'permit_expiry', label: 'Vencimiento Permiso', required: true, type: 'date' },
  { key: 'paut_number', label: 'N° PAUT', required: true }
];

export const TRUCK_FIELDS = [
  { key: 'license_plate', label: 'Patente', required: true, unique: true },
  { key: 'brand', label: 'Marca', required: true },
  { key: 'model', label: 'Modelo', required: true },
  { key: 'year', label: 'Año', required: true, type: 'number' },
  { key: 'chassis_number', label: 'N° Chasis', required: true },
  { key: 'international_insurance', label: 'Seguro Internacional', required: true },
  { key: 'insurance_expiry', label: 'Vencimiento Seguro', required: true, type: 'date' },
  { key: 'company_cuit', label: 'CUIT Empresa', required: true, reference: 'company' }
];

export const TRAILER_FIELDS = [
  { key: 'license_plate', label: 'Patente', required: true, unique: true },
  { key: 'brand', label: 'Marca', required: true },
  { key: 'international_insurance', label: 'Seguro Internacional', required: true },
  { key: 'insurance_expiry', label: 'Vencimiento Seguro', required: true, type: 'date' },
  { key: 'seal_quantity', label: 'Cantidad Precintos', required: true, type: 'number' },
  { key: 'company_cuit', label: 'CUIT Empresa', required: true, reference: 'company' }
];

export const DRIVER_FIELDS = [
  { key: 'name', label: 'Nombre', required: true },
  { key: 'dni', label: 'DNI', required: true, unique: true },
  { key: 'cuit_cuil', label: 'CUIT/CUIL', required: true },
  { key: 'phone', label: 'Teléfono', required: true },
  { key: 'company_cuit', label: 'CUIT Empresa', required: true, reference: 'company' }
];

// Estados iniciales de formularios
export const INITIAL_COMPANY_FORM = {
  // Datos de facturación
  name: '',           // Razón social del facturador
  alias: '',          // Alias del facturador
  cuit_rut: '',       // CUIT del facturador
  address: '', 
  city: '', 
  province: '',
  transportista_id: '',
  // Datos de permisos
  permits_company_id: '',    // ID de empresa con permisos (si usa otra)
  permit_holder_name: '',    // Razón social del dueño de permisos
  international_permit: '', 
  permit_expiry: '', 
  paut_number: ''
};

export const INITIAL_TRUCK_FORM = {
  license_plate: '', brand: '', model: '', year: new Date().getFullYear(),
  chassis_number: '', international_insurance: '', insurance_expiry: '', company_id: ''
};

export const INITIAL_TRAILER_FORM = {
  license_plate: '', brand: '', international_insurance: '', insurance_expiry: '', 
  seal_quantity: 0, company_id: ''
};

export const INITIAL_DRIVER_FORM = {
  name: '', dni: '', cuit_cuil: '', phone: '', company_id: ''
};

// Mapeo de tipos de entidad
export const ENTITY_CONFIG = {
  companies: {
    label: 'Empresas',
    singular: 'Empresa',
    fields: COMPANY_FIELDS,
    initialForm: INITIAL_COMPANY_FORM,
    endpoint: 'companies',
    bulkEndpoint: 'companies/bulk'
  },
  trucks: {
    label: 'Camiones',
    singular: 'Camión',
    fields: TRUCK_FIELDS,
    initialForm: INITIAL_TRUCK_FORM,
    endpoint: 'trucks',
    bulkEndpoint: 'trucks/bulk'
  },
  trailers: {
    label: 'Acoplados',
    singular: 'Acoplado',
    fields: TRAILER_FIELDS,
    initialForm: INITIAL_TRAILER_FORM,
    endpoint: 'trailers',
    bulkEndpoint: 'trailers/bulk'
  },
  drivers: {
    label: 'Choferes',
    singular: 'Chofer',
    fields: DRIVER_FIELDS,
    initialForm: INITIAL_DRIVER_FORM,
    endpoint: 'drivers',
    bulkEndpoint: 'drivers/bulk'
  }
};
